import { ElectronApi } from '@/services/electron.api';
import { ShapeType } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { isDirectory, isFile } from '@/utils/fs/fs-extra.utils';
import { getImageSize, isImageFile } from '@/utils/fs/image.utils';
import { pickZipExtensions, unZip } from '@/utils/fs/zip.utils';
import { validWatchfaceDir } from '@/utils/watchface-valid.utils';
import { Modal } from 'ant-design-vue';
import { useShapeManage, useWatchfaceImageValid, useWatchfaceManage } from '.';
import { GlobalConfig } from '@/services/gloabl.config';

export const useEditorDrag = () => {
  const { addShape } = useShapeManage();
  const { gotoWachfaceEditor, openLowerVersionWatchface } =
    useWatchfaceManage();
  const editorStore = useEditorStore();
  const { validImageSize } = useWatchfaceImageValid();

  const _openWatchfaceFile = async (filepath: string | undefined) => {
    let path = filepath;

    if (path && (await isFile(path)) && GlobalConfig.openZipEnabled) {
      path = await unZip(path);
    }
    if (!path) return;
    try {
      await validWatchfaceDir(path);
      // addItem(path);
      if (!ElectronApi.fs?.existsSync(path + '/editor.config.json')) {
        await openLowerVersionWatchface(path);
      } else {
        await gotoWachfaceEditor(path);
      }
    } catch (e) {
      Modal.error({
        title: '打开失败',
        content: e as string,
        okText: '关闭'
      });
      return;
    }
  };
  const onDropWatchfaceFile = async (e: DragEvent) => {
    const files = e.dataTransfer?.files;
    await _openWatchfaceFile(files?.[0].path);
  };

  const onDropImageFile = async (files: FileList) => {
    try {
      // 仅能在配色1下拖拽
      if (editorStore.themeIndex != 0) return;
      const fs = ElectronApi.fs;
      const path = ElectronApi.path;
      if (!fs || !path) return;

      // const files = e.dataTransfer?.files;
      if (!files?.length) return;

      const _addImage = async (imgPath: string) => {
        if (!isImageFile(imgPath)) return;

        // 获取图片大小
        const res = await getImageSize(imgPath);
        // 验证图片
        const valid = await validImageSize(imgPath);
        if (!valid) {
          console.log('图片验证失败，无法上传', imgPath);
          return;
        }

        if (!res) return;
        const shape = await addShape(ShapeType.Image, {
          attrs: {
            w: res.width,
            h: res.height,
            name: path.basename(imgPath).split('.')[0]
          }
        });
        await shape?.uploadResource({
          resourceName: 'pointer0',
          colorGroup: editorStore.themeColor?.id ?? '',
          realpath: imgPath
        });
      };

      for (let i = 0; i < files.length; i++) {
        const file = files[i].path;
        if ((await isFile(file)) || isImageFile(file)) {
          await _addImage(file);
        }

        if (await isDirectory(file)) {
          const subfiles = await fs.promises.readdir(file);
          for (const subfile of subfiles) {
            const _file = path.join(file, subfile);
            await _addImage(_file);
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onDropFileIntoEditor = async (e: DragEvent) => {
    const files = e.dataTransfer?.files;

    // 如果不存在文件，那么就直接返回就行，不需要拖拽实现
    if (!files?.length) return;

    const filepath = files[0]?.path;
    if (filepath && (await isFile(filepath)) && GlobalConfig.openZipEnabled) {
      // 如果是压缩包
      if (pickZipExtensions.some((item) => filepath.endsWith(`.${item}`))) {
        return await _openWatchfaceFile(filepath);
      }
    }
    return await onDropImageFile(files);
  };

  return {
    onDropWatchfaceFile,
    onDropFileIntoEditor
  };
};
